import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Accessibility,
  CaseSensitive,
  Contrast,
  MousePointer2,
  Pause,
  Play,
  RotateCcw,
  SkipForward,
  Square,
  Volume2,
  VolumeX,
  Wand2,
} from 'lucide-react';

const STORAGE_KEY = 'clear-horizons-accessibility-v4';

const defaultPreferences = {
  highContrast: true,
  largeText: true,
  reduceMotion: true,
  speakFocus: true,
  speakHover: true,
};

const readableSelector = [
  'button',
  'a',
  'input',
  'textarea',
  'select',
  'label',
  'h1',
  'h2',
  'h3',
  'h4',
  'p',
  'li',
  '[aria-label]',
  '[data-read-aloud]',
].join(',');

function getReadableMainText() {
  const main = document.querySelector('main');
  if (!main) return 'Main content is not available on this page.';

  return main.innerText
    .replace(/\s+/g, ' ')
    .replace(/Candidate login Learn about us/i, 'Candidate login. Learn about us.')
    .trim();
}

function loadPreferences() {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return stored ? { ...defaultPreferences, ...JSON.parse(stored) } : defaultPreferences;
  } catch {
    return defaultPreferences;
  }
}

function normalizeText(text) {
  return text?.replace(/\s+/g, ' ').trim() || '';
}

function getElementLabel(element) {
  if (!(element instanceof HTMLElement)) return '';

  const readableElement = element.closest(readableSelector);
  if (!(readableElement instanceof HTMLElement)) return '';

  if (readableElement.closest('[aria-hidden="true"]')) return '';

  const label =
    readableElement.getAttribute('aria-label') ||
    readableElement.getAttribute('title') ||
    document.querySelector(`label[for="${readableElement.id}"]`)?.textContent ||
    readableElement.getAttribute('placeholder') ||
    readableElement.innerText ||
    readableElement.textContent;

  return normalizeText(label).slice(0, 240);
}

function AccessibilityTools() {
  const [open, setOpen] = useState(false);
  const [preferences, setPreferences] = useState(loadPreferences);
  const [speaking, setSpeaking] = useState(false);
  const [paused, setPaused] = useState(false);
  const [announcement, setAnnouncement] = useState('Accessibility tools ready.');

  const speechSupported = useMemo(
    () => typeof window !== 'undefined' && 'speechSynthesis' in window && 'SpeechSynthesisUtterance' in window,
    []
  );

  useEffect(() => {
    document.documentElement.dataset.highContrast = preferences.highContrast ? 'true' : 'false';
    document.documentElement.dataset.largeText = preferences.largeText ? 'true' : 'false';
    document.documentElement.dataset.reduceMotion = preferences.reduceMotion ? 'true' : 'false';
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(preferences));
  }, [preferences]);

  const speak = useCallback((text, options = {}) => {
    if (!speechSupported) {
      setAnnouncement('Voice guide is not supported in this browser.');
      return;
    }

    if (options.interrupt) {
      window.speechSynthesis.cancel();
    }

    const utterance = new window.SpeechSynthesisUtterance(text);
    utterance.rate = options.rate || 0.95;
    utterance.pitch = 1;
    utterance.onstart = () => {
      setSpeaking(true);
      setPaused(false);
    };
    utterance.onend = () => {
      setSpeaking(false);
      setPaused(false);
    };
    utterance.onerror = () => {
      setSpeaking(false);
      setPaused(false);
      setAnnouncement('Voice guide could not read this content.');
    };

    window.speechSynthesis.speak(utterance);
  }, [speechSupported]);

  const readPage = useCallback(() => {
    const text = getReadableMainText();
    setAnnouncement('Reading main content aloud.');
    speak(text, { interrupt: true });
  }, [speak]);

  useEffect(() => {
    if (!speechSupported || !preferences.speakFocus) return undefined;

    const handleFocus = (event) => {
      const element = event.target;
      if (!(element instanceof HTMLElement)) return;

      const label = getElementLabel(element);

      if (label) {
        speak(`Focused ${label}`, { interrupt: true, rate: 1.05 });
      }
    };

    document.addEventListener('focusin', handleFocus);
    return () => document.removeEventListener('focusin', handleFocus);
  }, [preferences.speakFocus, speak, speechSupported]);

  useEffect(() => {
    if (!speechSupported || !preferences.speakHover) return undefined;

    let lastSpoken = '';
    let hoverTimer;

    const handlePointerOver = (event) => {
      window.clearTimeout(hoverTimer);

      hoverTimer = window.setTimeout(() => {
        const label = getElementLabel(event.target);

        if (!label || label === lastSpoken) return;

        lastSpoken = label;
        setAnnouncement(`Reading: ${label}`);
        speak(label, { interrupt: true, rate: 1.04 });
      }, 180);
    };

    document.addEventListener('pointerover', handlePointerOver);

    return () => {
      window.clearTimeout(hoverTimer);
      document.removeEventListener('pointerover', handlePointerOver);
    };
  }, [preferences.speakHover, speak, speechSupported]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (!event.altKey) return;

      if (event.key.toLowerCase() === 'v') {
        event.preventDefault();
        readPage();
      }

      if (event.key === '1') {
        event.preventDefault();
        document.getElementById('main-content')?.focus();
        setAnnouncement('Moved to main content.');
      }

      if (event.key === '2') {
        event.preventDefault();
        document.querySelector('nav a')?.focus();
        setAnnouncement('Moved to navigation.');
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [readPage]);

  const pauseSpeech = () => {
    if (!speechSupported) return;
    window.speechSynthesis.pause();
    setPaused(true);
    setAnnouncement('Voice guide paused.');
  };

  const resumeSpeech = () => {
    if (!speechSupported) return;
    window.speechSynthesis.resume();
    setPaused(false);
    setAnnouncement('Voice guide resumed.');
  };

  const stopSpeech = () => {
    if (!speechSupported) return;
    window.speechSynthesis.cancel();
    setSpeaking(false);
    setPaused(false);
    setAnnouncement('Voice guide stopped.');
  };

  const updatePreference = (key) => {
    setPreferences((current) => {
      const next = { ...current, [key]: !current[key] };
      setAnnouncement(`${key.replace(/([A-Z])/g, ' $1')} ${next[key] ? 'enabled' : 'disabled'}.`);
      return next;
    });
  };

  const resetPreferences = () => {
    stopSpeech();
    setPreferences(defaultPreferences);
    setAnnouncement('Accessibility preferences reset.');
  };

  const controls = [
    {
      key: 'highContrast',
      label: 'High contrast',
      description: 'Increases foreground contrast and simplifies surfaces.',
      icon: Contrast,
    },
    {
      key: 'largeText',
      label: 'Larger text',
      description: 'Raises base text size for low-vision reading.',
      icon: CaseSensitive,
    },
    {
      key: 'reduceMotion',
      label: 'Reduce motion',
      description: 'Minimizes animation and floating effects.',
      icon: Wand2,
    },
    {
      key: 'speakHover',
      label: 'Read under pointer',
      description: 'Reads the text, button, link, or field under the mouse.',
      icon: MousePointer2,
    },
    {
      key: 'speakFocus',
      label: 'Speak focus',
      description: 'Reads focused buttons and fields for keyboard users.',
      icon: Volume2,
    },
  ];

  return (
    <>
      <div className="sr-only" aria-live="polite" aria-atomic="true">
        {announcement}
      </div>

      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        aria-controls="accessibility-panel"
        className="focus-ring fixed bottom-5 right-5 z-50 flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-blue-600 via-teal-500 to-amber-400 text-white shadow-2xl shadow-blue-950/25 transition hover:-translate-y-1"
      >
        <Accessibility className="h-6 w-6" />
        <span className="sr-only">Open accessibility tools</span>
      </button>

      {open && (
        <aside
          id="accessibility-panel"
          aria-label="Accessibility tools"
          className="fixed bottom-24 right-4 z-50 w-[calc(100vw-2rem)] max-w-md overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-2xl shadow-slate-950/20"
        >
          <div className="color-strip h-1.5" />
          <div className="p-5">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="eyebrow">Accessibility</p>
                <h2 className="mt-2 text-xl font-semibold text-slate-950">Voice and vision tools</h2>
                <p className="mt-2 text-sm leading-6 text-slate-600">
                  Designed to work alongside VoiceOver, NVDA, JAWS, and browser keyboard navigation.
                </p>
              </div>
            </div>

            <div className="mt-5 rounded-2xl border border-slate-200 bg-slate-50 p-3">
              <p className="text-sm font-semibold text-slate-950">Voice Guide</p>
              <div className="mt-3 grid grid-cols-3 gap-2">
                <button
                  type="button"
                  onClick={paused ? resumeSpeech : readPage}
                  className="focus-ring inline-flex h-11 items-center justify-center gap-2 rounded-xl bg-slate-950 px-3 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:opacity-50"
                  disabled={!speechSupported}
                >
                  {paused ? <Play className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
                  {paused ? 'Resume' : 'Read'}
                </button>
                <button
                  type="button"
                  onClick={pauseSpeech}
                  className="focus-ring inline-flex h-11 items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-100 disabled:opacity-50"
                  disabled={!speechSupported || !speaking || paused}
                >
                  <Pause className="h-4 w-4" />
                  Pause
                </button>
                <button
                  type="button"
                  onClick={stopSpeech}
                  className="focus-ring inline-flex h-11 items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-100 disabled:opacity-50"
                  disabled={!speechSupported || (!speaking && !paused)}
                >
                  <Square className="h-4 w-4" />
                  Stop
                </button>
              </div>
              {!speechSupported && (
                <p className="mt-3 text-xs leading-5 text-red-600">
                  This browser does not support built-in speech synthesis.
                </p>
              )}
            </div>

            <div className="mt-4 grid gap-2">
              {controls.map((control) => {
                const Icon = control.icon;
                const enabled = preferences[control.key];
                return (
                  <button
                    key={control.key}
                    type="button"
                    onClick={() => updatePreference(control.key)}
                    aria-pressed={enabled}
                    className={[
                      'focus-ring flex items-center gap-3 rounded-2xl border p-3 text-left transition',
                      enabled
                        ? 'border-blue-200 bg-blue-50 shadow-sm shadow-blue-900/5'
                        : 'border-slate-200 bg-white hover:bg-slate-50',
                    ].join(' ')}
                  >
                    <span
                      className={[
                        'flex h-10 w-10 shrink-0 items-center justify-center rounded-xl',
                        enabled ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-600',
                      ].join(' ')}
                    >
                      <Icon className="h-5 w-5" />
                    </span>
                    <span>
                      <span className="block text-sm font-semibold text-slate-950">
                        {control.label}
                      </span>
                      <span className="block text-xs leading-5 text-slate-600">
                        {control.description}
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>

            <div className="mt-4 rounded-2xl bg-slate-950 p-4 text-white">
              <p className="text-sm font-semibold">Keyboard shortcuts</p>
              <div className="mt-3 space-y-2 text-sm text-slate-300">
                <p>
                  <kbd className="rounded bg-white/10 px-1.5 py-0.5 text-xs">Alt</kbd> +{' '}
                  <kbd className="rounded bg-white/10 px-1.5 py-0.5 text-xs">V</kbd> Read page
                </p>
                <p>
                  <kbd className="rounded bg-white/10 px-1.5 py-0.5 text-xs">Alt</kbd> +{' '}
                  <kbd className="rounded bg-white/10 px-1.5 py-0.5 text-xs">1</kbd> Main content
                </p>
                <p>
                  <kbd className="rounded bg-white/10 px-1.5 py-0.5 text-xs">Alt</kbd> +{' '}
                  <kbd className="rounded bg-white/10 px-1.5 py-0.5 text-xs">2</kbd> Navigation
                </p>
              </div>
            </div>

            <div className="mt-4 flex gap-2">
              <a
                href="#main-content"
                className="focus-ring inline-flex h-10 flex-1 items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
              >
                <SkipForward className="h-4 w-4" />
                Skip to main
              </a>
              <button
                type="button"
                onClick={resetPreferences}
                className="focus-ring inline-flex h-10 flex-1 items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
              >
                <RotateCcw className="h-4 w-4" />
                Reset
              </button>
            </div>

            <p className="mt-4 flex items-start gap-2 text-xs leading-5 text-slate-500">
              <VolumeX className="mt-0.5 h-4 w-4 shrink-0" />
              Voice Guide is optional. Native screen readers remain the best way to browse complex pages.
            </p>
          </div>
        </aside>
      )}
    </>
  );
}

export default AccessibilityTools;
